<script setup lang="ts">
import { computed, ref } from "vue";
import { dataTypesQuizQuestions } from "~/features/python/ch2/data-types/data/dataTypes.content";

const emit = defineEmits<{
  (event: "weak-topics", value: Array<"data-types" | "variables">): void;
}>();

const questions = dataTypesQuizQuestions.map((question) => ({
  id: question.id,
  question: question.question,
  options: question.options,
  correctIndex: question.correctIndex,
  explanation: question.explanation,
}));

const selections = ref<Record<string, number>>({});
const hasWrongAnswer = computed(() =>
  questions.some((question) => {
    const selected = selections.value[question.id];
    return selected !== undefined && selected !== question.correctIndex;
  }),
);

function selectAnswer(questionId: string, index: number) {
  if (selections.value[questionId] !== undefined) {
    return;
  }

  selections.value[questionId] = index;
  emit("weak-topics", hasWrongAnswer.value ? ["data-types", "variables"] : []);
}
</script>

<template>
  <section class="space-y-4 rounded-3xl border border-slate-200 bg-white p-5">
    <div class="space-y-1">
      <h2 class="text-lg font-semibold text-slate-900">Quick Quiz</h2>
      <p class="text-sm text-slate-600">5 sawalon se apni understanding check karo.</p>
    </div>

    <article
      v-for="question in questions"
      :key="question.id"
      class="rounded-2xl bg-slate-50 p-4 ring-1 ring-slate-200"
    >
      <p class="text-sm font-semibold text-slate-900">{{ question.question }}</p>

      <div class="mt-3 space-y-2">
        <button
          v-for="(option, index) in question.options"
          :key="`${question.id}-${index}`"
          type="button"
          class="w-full rounded-xl border px-3 py-2 text-left text-sm transition"
          :class="selections[question.id] === undefined
            ? 'border-slate-200 bg-white text-slate-700'
            : index === question.correctIndex
              ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
              : selections[question.id] === index
                ? 'border-rose-300 bg-rose-50 text-rose-800'
                : 'border-slate-200 bg-white text-slate-500'"
          :disabled="selections[question.id] !== undefined"
          @click="selectAnswer(question.id, index)"
        >
          {{ option }}
        </button>
      </div>

      <div v-if="selections[question.id] !== undefined" class="mt-3 rounded-xl bg-white p-3 text-sm">
        <p
          class="font-semibold"
          :class="selections[question.id] === question.correctIndex ? 'text-emerald-700' : 'text-rose-700'"
        >
          {{ selections[question.id] === question.correctIndex ? "Correct" : "Incorrect" }}
        </p>
        <p class="mt-1 text-slate-700">{{ question.explanation }}</p>
      </div>
    </article>
  </section>
</template>
